import React, { useState, useEffect } from "react";
import ColorPicker from "./ColorPicker";
import CategorySelector from "./CategorySelector";
import { useGlobalState } from "../contexts/GlobalStateContext";
import ChooseImage from "./ChooseImage";
import MyEditor from "./MyEditor";
import Post from "./Post";
import { useRef } from "react";
import SimpleConfirmation from "./SimpleConfirmation";
import useScrollLockOnMount from "../Helpers/utils";
import AddMedia from "./AddMedia";
import FetchPosts from "../Helpers/fetchPosts";
const Create = ({ uuid, onAction, onClose }) => {
  const {
    globalState,
    setShowLogins,
    setShowLogin,
    setMessageJD,
    logOut,
    resetGlobalState,
    setRefreshKeyForPage,
    setSessionExpired,
    setJustPosted,
    incrementPostCount,
    setGlobalState,
    setFullScreenPost
  } = useGlobalState();
  const [textInput, setTextInput] = useState("");
  const [imageSelected, setImageSelected] = useState(false);
  const [imageSrc, setImageSrc] = useState("/thumbnails/default_thumbnail.jpg");
  const [showImageModal, setShowImageModal] = useState(false);
  const [link, setLink] = useState(null);
  const [classNumber, setClassNumber] = useState(2);
  const [final_abbreviation, setFinal_abbreviation] = useState("");
  const [wordCount, setWordCount] = useState(3);
  const [characterCount, setCharacterCount] = useState(14);
  const [includeStopWords, setIncludeStopWords] = useState(true);
  const [includeAbvri, setIncludeAbvri] = useState(true);
  const [themeColor, setThemeColor] = useState("#90EE90");
  const [complementaryColor, setComplementaryColor] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [blurValue, setBlurValue] = useState(0);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editorContent, setEditorContent] = useState("");
  const increaseBlur = () => setBlurValue((prev) => prev + 1);
  const decreaseBlur = () => setBlurValue((prev) => Math.max(0, prev - 1));
  const [publishLoadingText, setPublishLoadingText] = useState("");
  const [draftLoadingText, setDraftLoadingText] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(true);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);

  // Effect to load draft data if UUID is provided
  useEffect(() => {
    if (uuid) {
      // Added baseUrl check for robustness
      setIsLoadingDraft(true);

      // Fetch draft post data
      const fetchDraftPromise = fetch(`/php/fetch_draft.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ uuid }),
      })
        .then(async (response) => {
          if (!response.ok) {
            // Try to get more error info if possible
            const text = await response.text();
            throw new Error(
              `Network response was not ok for fetch_draft. Status: ${response.status}. Message: ${text}`
            );
          }
          // ---- END DEBUGGING LOGS ----
          return response.json();
        })
        .then((data) => {
          if (data && data.post) {
            const post = data.post;

            const fullForm = post.full_form || "";
            const isAbvriIncluded =
              post.abbreviation !== null && post.abbreviation !== undefined;

            setTextInput(fullForm);
            setRawTextInput(toTitleCase(fullForm)); // Assuming toTitleCase is defined
            setLink(post.link || "#");
            setIncludeAbvri(isAbvriIncluded);
            setThemeColor(post.theme || "#90EE90");
            setSelectedData({
              context: post.context || null,
              category: post.category_id || null,
              subcategory: post.subcategory_id || null,
            });
            setBlurValue(Number(post.blur) || 0);

            const draftImageSrc = `/thumbnails/${uuid}.jpg`;
            setImageSrc(draftImageSrc);

            const imageCheck = new Image();
            imageCheck.onload = () => {
              setImageSelected(true);
            };
            imageCheck.onerror = () => {
              setImageSelected(false);
              console.warn(
                "[Effect Load Draft] Failed to load image:",
                draftImageSrc
              );
            };
            imageCheck.src = draftImageSrc;
          } else {
            console.error(
              "[Effect Load Draft] Draft data.post not found or invalid format:",
              data
            );
          }
        })
        .catch((error) => {
          console.error("[Effect Load Draft] Error fetching draft:", error);
        });

      // Fetch more info (editor content)
      const fetchMoreInfoPromise = fetch(
        `/php/fetch_more_info.php?uuid=${uuid}`
      )
        .then(async (response) => {
          if (!response.ok) {
            const text = await response.text();
            console.error(
              "[Effect Load Draft] fetch_more_info.php response text (if not ok):",
              text
            );
            throw new Error(
              `Network response was not ok for fetch_more_info. Status: ${response.status}. Message: ${text}`
            );
          }
          // ---- END DEBUGGING LOGS ----
          return response.json();
        })
        .then((data) => {
          // ---- END DEBUGGING LOGS ----
          if (data && data.details !== undefined) {
            setEditorContent(data.details || "");
          } else {
            setEditorContent("");
            console.warn(
              "[Effect Load Draft] More info (details) not found or invalid format for UUID:",
              uuid,
              data
            );
          }
        })
        .catch((error) => {
          console.error("[Effect Load Draft] Error fetching more info:", error);
        });

      Promise.allSettled([fetchDraftPromise, fetchMoreInfoPromise]).finally(
        () => {
          setIsLoadingDraft(false);
        }
      );
    } else if (uuid) {
      console.error(
        "[Effect Load Draft] UUID is present but baseUrl is not defined. Check REACT_APP_BASE_URL environment variable."
      );
      setIsLoadingDraft(false); // Stop loading if base URL is missing
    } else if (!uuid) {
      setIsLoadingDraft(false);
    }
  }, [uuid]); // Added baseUrl to dependency array

  const handleCategorySelect = (data) => {
    setSelectedData(data);
    setShowCategorySelector(false); // Close the modal after selection
  };

  const handleSave = (content) => {
    setEditorContent(content);
    setShowEditor(false); // Hide the editor after saving
  };

  const handleCancel = () => {
    setShowEditor(false); // Hide the editor without saving
  };

  const handleCrop = (croppedImage) => {
    // Set the cropped image as the source
    setImageSrc(croppedImage);
    setShowImageModal(false);
    setImageSelected(true);
  };

  // Function to calculate black or white based on contrast
  const getContrastColor = (hex) => {
    // Convert hex to RGB
    let r = parseInt(hex.substring(1, 3), 16);
    let g = parseInt(hex.substring(3, 5), 16);
    let b = parseInt(hex.substring(5, 7), 16);

    // Calculate luminance (Perceived brightness)
    let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // If luminance is low, return white (#FFFFFF), else return black (#000000)
    return luminance < 0.5 ? "#FFFFFF" : "#000000";
  };

  // Update contrast color whenever themeColor changes
  useEffect(() => {
    setComplementaryColor(getContrastColor(themeColor));
  }, [themeColor]);

  const handleCheckboxChange = (event) => {
    setIncludeStopWords(event.target.checked); // Updates state based on checkbox status
  };

  const handleIncludeAbvriCheckboxChange = (event) => {
    setIncludeAbvri(event.target.checked); // Updates state based on checkbox status
  };

  const [rawTextInput, setRawTextInput] = useState("");
  const [showDialog, setShowDialog] = useState(false);

  const nonEnglishRegex =
    /[^\x20-\x7E\u00A0-\u00FF\u0100-\u017F\u2000-\u200B\u2010-\u201F\u2022\u2026\u2100-\u214F\u2190-\u21FF\u2200-\u22FF\u2300-\u23FF\u25A0-\u25FF\u20AC\u00A3\u00A5\u2122\u00AE\u00A9]/;

  const handleTextInputChange = (e) => {
    const value = e.target.value;

    const isPaste = e.nativeEvent.inputType === "insertFromPaste";

    if (isPaste) {
      // Check the entire pasted content
      if (nonEnglishRegex.test(value.trim())) {
        setShowDialog(true);
        return;
      }
    } else {
      // Typing: check only when a word is completed (ends in space or punctuation)
      const wordBoundaryPattern = /[ \t\n,.!?;:)\]}]/;
      const lastChar = value.slice(-1);

      if (wordBoundaryPattern.test(lastChar)) {
        const words = value.trim().split(/\s+/);
        const lastWord = words[words.length - 1];

        if (nonEnglishRegex.test(lastWord)) {
          setShowDialog(true);
          return;
        }
      }
    }

    const rawValue = toTitleCase(value);
    setRawTextInput(rawValue);

    const trimmedValue = trimInput(rawValue, includeAbvri);
    setTextInput(trimmedValue);
  };

  const trimInput = (value, includeAbvri) => {
    const wordLimit = includeAbvri ? 15 : 25;
    const charLimit = includeAbvri ? 101 : 200;

    // Limit characters first (this helps avoid half-word issues when counting words)
    let charTrimmed = value.slice(0, charLimit);

    // Then limit words
    let words = charTrimmed.match(/\S+/g) || []; // match non-whitespace sequences
    if (words.length > wordLimit) {
      let allowedWords = words.slice(0, wordLimit);
      // Preserve any trailing space if user was typing one
      const lastChar = charTrimmed[charTrimmed.length - 1];
      return allowedWords.join(" ") + (lastChar === " " ? " " : "");
    }

    return charTrimmed;
  };

  // Re-trim when abvri setting changes
  useEffect(() => {
    setTextInput(trimInput(rawTextInput, includeAbvri));
  }, [includeAbvri]);

  const handleSubmit = (type) => {
    if (nonEnglishRegex.test(textInput)) {
      setShowDialog(true);
      return;
    }

    if (!imageSelected) {
      setShowImageModal(true);
      return;
    }
    const jsonData = {
      ...(uuid && { uuid }),
      FF: textInput,
      theme: themeColor,
      post_class: classNumber,
      text_bg: complementaryColor,
      blur: blurValue,
      ...(link && { link }),
      ...(uuid && { uuid }),
      ...(includeAbvri && { FA: final_abbreviation }),
      editorContent,
      ...(selectedData?.context && { context: selectedData.context }),
      ...(selectedData?.category && { category_id: selectedData.category }),
      ...(selectedData?.subcategory && {
        subcategory_id: selectedData.subcategory,
      }),
    };

    const endpoint = `/php/${type === "publish" ? "post.php" : "postDraft.php"
      }`;

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(jsonData),
    })
      .then(async function (response) {
        const data = await response.json();
        console.log("Data received from server:", data);
        if (!response.ok) {
          if (response.status === 401) {
            setSessionExpired(true);
            return;
          }

          throw new Error(data.message || "Unknown error occurred");
        }

        return data;
      })
      .then((data) => {
        if (!data) return;

        switch (data.status) {
          case "duplicate":
            setMessageJD("Duplicate Post Detected. Showing existing post.");
            setJustPosted(data.post);
            break;

          case "error":
            logOut();
            resetGlobalState();
            break;

          case "success":
          default:
            uploadCroppedImage(type, data.post);
            break;
        }
      })
      .catch((error) => {
        console.error("Submission error:", error);
      });
  };

  const uploadCroppedImage = async (type, post) => {
    let interval;
    if (type === "publish") {
      setPublishLoadingText("Publishing");
      interval = setInterval(() => {
        setPublishLoadingText((prev) =>
          prev.length < "Publishing....".length ? prev + "." : "Publishing"
        );
      }, 500);

      // Don't forget to clear interval later
    } else {
      setDraftLoadingText("Saving Draft");
      interval = setInterval(() => {
        setDraftLoadingText((prev) =>
          prev.length < "Saving Draft....".length ? prev + "." : "Saving Draft"
        );
      }, 500);

      // Also clear interval later
    }

    try {
      const response = await fetch(imageSrc);
      const imageBlob = await response.blob();

      const formData = new FormData();
      formData.append("image", imageBlob, "croppedImage.jpg");

      const uploadResponse = await fetch(`/php/upload.php`, {
        method: "POST",
        body: formData,
      });

      if (uploadResponse.ok) {
        setShowCreateModal(false);
        setRefreshKeyForPage(type === "publish" ? "posts" : "drafts");
        setMessageJD(
          type === "publish"
            ? "Successfully Added."
            : uuid
              ? "Draft Updated."
              : "Draft Saved. "
        );

        // This is the crucial change
        setJustPosted(null);
        await new Promise(resolve => setTimeout(resolve, 50)); // Wait for a short time
        incrementPostCount();
        setJustPosted(post);
        setGlobalState(prev => ({
          ...prev,
          refreshKey: 8
        }));

      } else if (uploadResponse.status === "error") {
        logOut();
        resetGlobalState();
      } else {
        throw new Error(`HTTP Error: ${uploadResponse.status}`);
      }
    } catch (error) {
      console.error("Image upload failed:", error);
    } finally {
      clearInterval(interval);
      setPublishLoadingText("");
      setDraftLoadingText("");
    }
  };

  const stopWordsList = [
    "a",
    "an",
    "and",
    "as",
    "at",
    "by",
    "for",
    "from",
    "in",
    "of",
    "on",
    "or",
    "the",
    "to",
    "with",
  ];

  function toTitleCase(inputString) {
    return inputString.replace(
      /\w\S*/g,
      (word) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()
    );
  }

  function determineClassNumber(charCount) {
    if (charCount <= 10) return 2;
    if (charCount <= 20) return 3;
    if (charCount <= 40) return 5;
    if (charCount <= 50) return 6;
    if (charCount <= 60) return 7;
    if (charCount <= 70) return 8;
    if (charCount <= 80) return 9;
    if (charCount <= 90) return 10;
    if (charCount <= 100) return 11;
    return 12;
  }

  useEffect(() => {
    // Prepare words array
    const words = textInput.split(" ").filter((word) => word !== "");

    // Update counts
    setWordCount(words.length);
    setCharacterCount(textInput.length);

    // Convert to title case
    const fullFormText = toTitleCase(textInput);

    // Sanitize input (remove quotes and unwanted characters)
    const sanitizedInput = fullFormText.replace(/['"‘’“”＂＇]/g, "");

    // Generate abbreviation
    const wordArray = sanitizedInput.split(" ").filter((word) => word !== "");
    const filteredWords = includeStopWords
      ? wordArray
      : wordArray.filter((word) => !stopWordsList.includes(word.toLowerCase()));

    setFinal_abbreviation(
      filteredWords
        .map((word) => word[0])
        .slice(0, 15)
        .join("")
        .toUpperCase()
    );

    // Update class number based on character count
    const newClassNumber = determineClassNumber(textInput.length);
    if (newClassNumber !== classNumber) {
      setClassNumber(newClassNumber);
    }
  }, [textInput, includeStopWords]);

  const textareaRef = useRef(null);

  // Auto-resize function for the textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // Reset height to auto
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Set to scrollHeight
    }
  }, [textInput]);

  const handleSaveLink = () => {
    setShowLinkInput(false); // Hide the link input after saving
  };

  useEffect(() => {
    if (!globalState.isAuthenticated) {
      setShowLogin(true);
      setShowLogins(true); // Show login modal if not authenticated
    } else {
      setShowCreateModal(true);
    }
  }, [globalState.isAuthenticated]); // Dependency on isAuthenticated

  const closeJustPostedModal = () => {
    setJustPosted(null);
    onClose();
  };

  const isMobile =
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  useEffect(() => {
    if (isMobile && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleFocus = () => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300); // Give time for keyboard layout to settle
  };

  const handleOnClose = () => {
    onClose();
  };

  useScrollLockOnMount();

  if (isLoadingDraft && uuid) {
    return (
      <div
        id="create_post_modal"
        className={`${isMobile ? "move-to-top" : "centered"}`}
        style={{ display: "block" }}
      >
        <div className="w3-modal-content w3-animate-bottom">
          <header className="w3-container w3-teal">
            <div className="toggle_row">
              <div
                className="columnA"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: "25px",
                }}
              >
                <span style={{ fontSize: "20px", marginLeft: "5px" }}>
                  Edit Draft
                </span>
                <span
                  onClick={handleOnClose}
                  style={{
                    fontSize: "40px",
                    cursor: "pointer",
                    marginTop: isMobile ? "-7.5px" : "0px",
                  }}
                >
                  ×
                </span>
              </div>
            </div>
          </header>
          <div className="w3-container">
            <div className="draftContainer">
              <div className="image-wrapper">
                <div className="image-placeholder" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If themeColor or complementaryColor is causing a crash, ensure they are always valid hex strings.
  // Defensive fallback for themeColor and complementaryColor:
  const safeThemeColor = /^#[0-9A-F]{6}$/i.test(themeColor) ? themeColor : "#90EE90";
  const safeComplementaryColor = /^#[0-9A-F]{6}$/i.test(complementaryColor) ? complementaryColor : "#000000";

  return (
    <>
      <SimpleConfirmation
        show={showDialog}
        onConfirm={() => setShowDialog(false)}
      />

      {globalState.justPosted && (
        <div className="justPosted-modal-overlay">
          <div className="duplicate-modal-content">
            <span
              style={{
                display: "block",
                margin: "5px auto 10px auto",
                color: "green",
              }}
            >
              {globalState.messageJD}
            </span>
            <button
              className="duplicate-modal-close"
              onClick={() => closeJustPostedModal()}
            >
              &times;
            </button>
            <Post
              key={globalState.justPosted.uuid}
              post={globalState.justPosted}
              onImageClick={() => {
                setFullScreenPost({
                  ...globalState.justPosted,
                  isExternalClick: true
                }); closeJustPostedModal();
              }}
            />
          </div>
        </div>
      )}

      {showEditor && (
        <MyEditor
          onSave={handleSave}
          onCancel={handleCancel}
          initialContent={editorContent}
        />
      )}

      {showImageModal && (
        <ChooseImage
          defaultImage={imageSrc}
          onClose={() => setShowImageModal(false)}
          onCrop={handleCrop}
        />
      )}
      {showLinkInput && (
        <div id="add_link" className="popup" style={{ display: "block" }}>
          <span
            className="closeLink"
            onClick={() => setShowLinkInput(false)}
            style={{ cursor: "pointer" }}
          >
            ×
          </span>
          <br />
          <input
            type="text"
            placeholder="Member's email address"
            id="linkInput"
            className="fullTextbox"
            autoComplete="off"
            value={link || ""}
            onChange={(e) => setLink(e.target.value)}
          />
          <br />
          <button
            className="save"
            id="saveLink"
            onClick={handleSaveLink}
          >
            Save
          </button>
        </div>
      )}
      {showCategorySelector && (
        <CategorySelector
          onClose={() => setShowCategorySelector(false)}
          onSelect={handleCategorySelect}
        />
      )}

      {globalState.user && showCreateModal && (
        <div className="overlay">
          <div
            id="create_post_modal"
            className={`${isMobile ? "move-to-top" : "centered"}`}
            style={{ display: "block" }}
          >
            <div className="w3-modal-content w3-animate-bottom">
              <header className="w3-container w3-teal">
                <div className="toggle_row">
                  <div
                    className="columnA"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      height: "25px",
                    }}
                  >
                    <span style={{ fontSize: "20px", marginLeft: "5px" }}>
                      Create A Post
                    </span>
                    <span
                      onClick={handleOnClose}
                      style={{
                        fontSize: "40px",
                        cursor: "pointer",
                        marginTop: isMobile ? "-7.5px" : "0px",
                      }}
                    >
                      ×
                    </span>
                  </div>
                </div>
              </header>
              <div className="w3-container">
                <div className="draftContainer">
                  <button id="minus-blur" onClick={() => decreaseBlur()}>
                    -Blur
                  </button>
                  <button id="add-blur" onClick={() => increaseBlur()}>
                    +Blur
                  </button>
                  <img
                    src={imageSrc}
                    id="defaultThumbnail"
                    alt="Snow"
                    className="thumbnail"
                    style={{ filter: `blur(${blurValue}px)` }}
                  />

                  <div className="bottom-left" id="draft_bottom_left" style={{
                    backgroundColor: safeThemeColor,
                    color: safeComplementaryColor,
                  }}>
                    Like
                  </div>
                  <div className="top-left">
                    <img
                      alt="Create"
                      id="dp"
                      className="dp"
                      width="25px"
                      src="/icon/create_icon.png"
                    />
                    <span
                      id="username"
                      style={{ cursor: "pointer", color: safeComplementaryColor, backgroundColor: safeThemeColor, padding: '0 5px' }}
                    >
                      {globalState.user.username || ""}
                    </span>
                  </div>
                  <div className="top-right">
                    <ColorPicker
                      onColorSelect={setThemeColor}
                      defaultColor={safeThemeColor}
                    />
                  </div>
                  <div className="bottom-right" id="draft_bottom_right" style={{
                    backgroundColor: safeThemeColor,
                    color: safeComplementaryColor,
                  }}>
                    <span>Share</span>
                  </div>
                  <div
                    className="context-container"
                    onClick={() => setShowCategorySelector(true)}
                  >
                    <svg
                      height="20"
                      width="20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <polygon
                        id="left-polygon"
                        points="0,0 10,10 0,20 20,20 20,0"
                        style={{ fill: safeThemeColor }}
                      />
                    </svg>
                    <span
                      id="draft-context"
                      className="context"
                      style={{
                        backgroundColor: safeThemeColor,
                        color: safeComplementaryColor,
                      }}
                    >
                      {selectedData?.context ?? "Nickname"}

                    </span>
                    <svg
                      height="20"
                      width="20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <polygon
                        id="right-polygon"
                        points="0,0 0,20 20,20 15,10 20,0"
                        style={{ fill: safeThemeColor }}
                      />
                    </svg>
                  </div>

                  <div
                    className={
                      includeAbvri ? "centered" : "centered_without_abvri"
                    }
                  >
                    <span
                      id="draft_abvri"
                      className={`abvri${classNumber}`}
                      style={{
                        color: safeThemeColor,
                        display: includeAbvri ? "inline" : "none",
                      }}
                    >
                      {final_abbreviation || "FN"}
                    </span>
                    <br />
                    <span
                      id="draft_fullForm"
                      className={`fullForm${classNumber}`}
                      style={{
                        backgroundColor: safeComplementaryColor,
                        color: safeThemeColor,
                        cursor: "pointer",
                      }}
                      onClick={() => setShowEditor(true)}
                    >
                      {textInput || "Full Name"}
                    </span>
                    <br></br>
                  </div>

                  <span
                    className="link"
                    id="draft_link"
                    onClick={() => setShowLinkInput(true)}
                    style={{ cursor: "pointer" }}
                  >
                    🔗
                  </span>
                  <button
                    id="uploadImg"
                    onClick={() => setShowImageModal(true)}
                  >
                    Upload an Image
                  </button>
                  <div className="footer" id="draft_footer">
                    <span id="likes">999 Likes | </span>
                    <span id="draft_comments">999 Comments</span>
                  </div>
                </div>

                <div className="inputTextContainer">
                  <div className="textarea-container">
                    <textarea
                      autoComplete="off"
                      ref={textareaRef}
                      type="text"
                      placeholder="Enter Member's Full Name"
                      rows="1"
                      id="textInput"
                      value={textInput}
                      onFocus={handleFocus}
                      onChange={handleTextInputChange}
                    />

                    <img
                      src="/icon/more_info.svg"
                      height={30}
                      width={30}
                      className="more_info"
                      onClick={() => setShowEditor(true)}
                      alt="More Info"
                    />
                  </div>

                  <div className="output-container">
                    <div className="output">
                      <p className="label">Chars</p>
                      <span>
                        {characterCount}/{includeAbvri ? 101 : 200}
                      </span>
                    </div>
                    <div className="output">
                      <p className="label">Words</p>
                      <span>
                        {wordCount}/{includeAbvri ? 15 : 25}
                      </span>
                    </div>

                    <label
                      id="include_abvri"
                      className="output"
                      htmlFor="includeStopWords"
                    >
                      <input
                        type="checkbox"
                        id="includeStopWords"
                        checked={includeAbvri}
                        onChange={handleIncludeAbvriCheckboxChange}
                      />
                      Include
                      <br />
                      &nbsp;&nbsp;&nbsp; abbreviation
                    </label>
                    <label
                      id="stopWords"
                      className="output"
                      htmlFor="includeStopWords"
                    >
                      <input
                        type="checkbox"
                        id="includeStopWords"
                        checked={includeStopWords}
                        onChange={handleCheckboxChange}
                        disabled={!includeAbvri}
                      />
                      Include Stop Words
                      <br />
                      &nbsp;&nbsp;&nbsp; in the abbreviation
                    </label>
                  </div>
                </div>
              </div>

              <div className="centered-buttons">
                <button
                  id="save_as_draft"
                  style={{ backgroundColor: "gray" }}
                  onClick={() => handleSubmit("draft")}
                >
                  {draftLoadingText || "Save As Draft"}
                </button>{" "}
                <button id="publish" onClick={() => handleSubmit("publish")}>
                  {publishLoadingText || "Publish Now"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Create;
